"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Megaphone, Palette, X } from "@/lib/icons"
import { cn } from "@/lib/utils"
import { setPromoReferral } from "@/lib/promo-referral"
import { useTranslations } from "next-intl"

export interface CommissionPromoAd {
  promotion_id: string
  artist_id: string
  username: string | null
  display_name: string | null
  avatar_url: string | null
  banner_url: string | null
  /** Purpose-cropped image for the square grid layout — falls back to
   * banner_url when a creator only supplied one image. */
  banner_url_square?: string | null
  store_headline: string | null
  store_path: string
  /** Link copy at the bottom of the card — defaults to "View storefront".
   * A game promo's serve route sends "Play now" instead, since store_path
   * points at /games/[id] rather than a storefront for that entity type. */
  link_label?: string
  /** Set when the creator's ad_image_url already has its own copy/branding
   * baked in — suppresses our own name/headline overlay so the two don't
   * compete for the same space. */
  hide_text_overlay?: boolean
  /** Hides just the "Promo" corner badge — independent of the text overlay. */
  hide_badge?: boolean
  /** Hides just the dark gradient wash behind the text overlay — independent
   * of whether the text itself shows. */
  hide_dim_overlay?: boolean
  is_promo: true
}

interface CommissionPromoCardProps {
  promo: CommissionPromoAd
  onDismiss?: () => void
  aspectRatio?: "square" | "portrait" | "landscape"
  className?: string
  /** Set when this render is a live preview in the promote dialog, not a
   * real placement — skips impression/click tracking so a fake preview
   * promo id never hits the real analytics endpoint. */
  disableTracking?: boolean
}

/** Artist-storefront promotion card for the main content feed — same visual
 * language and impression-tracking pattern as ScriptPromoCard, just linking
 * out to the artist's store instead of a buy/try flow. */
export function CommissionPromoCard({ promo, onDismiss, aspectRatio = "portrait", className, disableTracking = false }: CommissionPromoCardProps) {
  const t = useTranslations("commissionPromoCard")
  const [impressionRecorded, setImpressionRecorded] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (impressionRecorded || disableTracking) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          recordImpression()
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    if (cardRef.current) observer.observe(cardRef.current)
    return () => observer.disconnect()
  }, [impressionRecorded, disableTracking])

  const recordImpression = async () => {
    setImpressionRecorded(true)
    try {
      await fetch("/api/commissions/ad-promotions/serve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ promotion_id: promo.promotion_id }),
      })
    } catch {
      // best-effort tracking, never block the UI
    }
  }

  const handleClick = () => {
    if (disableTracking) return
    setPromoReferral(promo.artist_id, promo.promotion_id)
    fetch("/api/commissions/ad-promotions/serve", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ promotion_id: promo.promotion_id, was_clicked: true }),
    }).catch(() => {})
  }

  const aspectRatioClass = { square: "aspect-square", portrait: "aspect-[3/4]", landscape: "aspect-video" }[aspectRatio]

  return (
    <Card ref={cardRef} className={cn("group relative overflow-hidden border-primary/30 hover:border-primary/60 transition-all py-0 gap-0", className)}>
      {!promo.hide_badge && (
        <div className="absolute top-2 left-2 z-10">
          <Badge className="bg-primary/90 text-primary-foreground backdrop-blur-sm">
            <Megaphone className="h-3 w-3 mr-1" />
            {t("promo")}
          </Badge>
        </div>
      )}

      {onDismiss && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 z-10 h-6 w-6 bg-background/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => { e.stopPropagation(); onDismiss() }}
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      <Link href={promo.store_path} onClick={handleClick} className={cn("relative block", aspectRatioClass)}>
        <Image
          src={(aspectRatio === "square" ? promo.banner_url_square : null) || promo.banner_url || promo.avatar_url || "/placeholder.svg?height=400&width=300&query=artist storefront"}
          alt={promo.display_name || t("artistStorefront")}
          fill
          className="object-cover"
        />
        {!promo.hide_dim_overlay && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        )}
        {!promo.hide_text_overlay && (
          <div className="absolute inset-0 flex flex-col justify-end p-3">
            <div className="flex items-center gap-2 mb-1.5">
              {promo.avatar_url && (
                <div className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full border border-white/40">
                  <Image src={promo.avatar_url} alt="" fill className="object-cover" />
                </div>
              )}
              <h3 className="text-white font-semibold text-sm line-clamp-1">{promo.display_name || promo.username}</h3>
            </div>
            <p className="text-white/75 text-xs line-clamp-2">{promo.store_headline || "Open for commissions"}</p>
            <div className="mt-2 flex items-center gap-1.5 text-white/90 text-xs font-medium">
              <Palette className="h-3.5 w-3.5" />
              {promo.link_label || "View storefront"}
            </div>
          </div>
        )}
      </Link>
    </Card>
  )
}
